import type { INotification } from '@pulse/shared';
import type { Notification } from '@pulse/ui';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  dismissNotification,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../features/notifications/api/notifications.api';
import { NOTIFICATIONS_KEY } from '../features/notifications/hooks/useNotificationsList';
import { useNotificationStream } from '../features/notifications/hooks/useNotificationStream';
import { toUiNotification } from '../features/notifications/utils/notification-adapter';

interface State {
  notifications: Notification[];
  unreadCount: number;
}

type Action =
  | { type: 'HYDRATE'; notifications: Notification[]; unreadCount: number }
  | { type: 'ADD'; notification: Notification }
  | { type: 'MARK_READ'; id: string }
  | { type: 'MARK_ALL_READ' }
  | { type: 'DISMISS'; id: string }
  // SYNC_* variants back a server-confirmed change made through the *other* notification
  // surface (the full Notifications page's own React Query mutations, see
  // useNotificationActions.ts) — the panel's `notifications` list only ever holds the 20 most
  // recent *unread* items (see PANEL_PAGE_SIZE and hydrate() below), so unlike MARK_READ/DISMISS
  // above, these can't
  // gate the unreadCount adjustment on finding a matching local item first: the page already
  // knows the item's prior read state (it's what decided which action to call), so these trust
  // that and adjust the count unconditionally, only patching the local list entry if present.
  | { type: 'SYNC_READ'; id: string }
  | { type: 'SYNC_UNREAD'; id: string }
  | { type: 'SYNC_DISMISS'; id: string; wasUnread: boolean };

// `unreadCount` is tracked alongside the list, not derived from it, because the panel only ever
// holds a recent slice of notifications (see the fetchNotifications call below) while the count
// must reflect the recipient's true total unread — deriving it from the slice would under-count
// once older unread notifications scroll out of that slice.
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'HYDRATE':
      return { notifications: action.notifications, unreadCount: action.unreadCount };
    case 'ADD':
      return { notifications: [action.notification, ...state.notifications], unreadCount: state.unreadCount + 1 };
    case 'MARK_READ': {
      const target = state.notifications.find((n) => n.id === action.id);
      if (!target || target.read) return state;
      return {
        notifications: state.notifications.map((n) => (n.id === action.id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }
    case 'MARK_ALL_READ':
      return { notifications: state.notifications.map((n) => ({ ...n, read: true })), unreadCount: 0 };
    case 'DISMISS': {
      const target = state.notifications.find((n) => n.id === action.id);
      const wasUnread = !!target && !target.read;
      return {
        notifications: state.notifications.filter((n) => n.id !== action.id),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }
    case 'SYNC_READ':
      return {
        notifications: state.notifications.map((n) => (n.id === action.id ? { ...n, read: true } : n)),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case 'SYNC_UNREAD':
      return {
        notifications: state.notifications.map((n) => (n.id === action.id ? { ...n, read: false } : n)),
        unreadCount: state.unreadCount + 1,
      };
    case 'SYNC_DISMISS':
      return {
        notifications: state.notifications.filter((n) => n.id !== action.id),
        unreadCount: action.wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    default:
      return state;
  }
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  // Re-fetches the panel's list + unread count from the server right now, rather than waiting
  // on an SSE push, a reconnect, or the tab regaining focus — for callers that just performed a
  // mutation they already know produces a notification for the current user (e.g.
  // ProjectRequestsPage closing/creating its own request) and want the bell to reflect it with
  // no race against SSE timing at all.
  refresh: () => Promise<void>;
  // Local-state-only versions of the above — no API call, since the caller (the full
  // Notifications page's useNotificationActions(), a separate React Query consumer that never
  // touches this reducer) already made and confirmed that call itself. Without these, the bell
  // badge only ever caught up with actions taken on that page by coincidence, on the next SSE
  // reconnect's hydrate() — not on the action itself.
  syncRead: (id: string) => void;
  syncUnread: (id: string) => void;
  syncAllRead: () => void;
  syncDismiss: (id: string, wasUnread: boolean) => void;
}

const NotificationContext = React.createContext<NotificationContextValue | undefined>(undefined);

const PANEL_PAGE_SIZE = 20;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, { notifications: [], unreadCount: 0 });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The mirror image of useNotificationActions()'s invalidate(): that hook syncs the full
  // Notifications page's actions *into* this reducer via sync*(), but the page reads its list
  // through React Query, so an action taken *here* left that cache untouched — the row stayed
  // visibly Unread on the page until the query happened to refetch. Same handshake, other
  // direction.
  const invalidatePage = React.useCallback(
    () => void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] }),
    [queryClient],
  );

  // Shared by the initial mount hydrate and by SSE reconnects (see useNotificationStream's
  // onReconnect below) — a dropped-then-restored connection never replays what fired while it
  // was down, so re-running this exact fetch is what catches the panel back up.
  const hydrate = React.useCallback(async () => {
    // `isRead: false` scopes the window to unread items rather than the newest ones overall.
    // Without it this fetched the 20 most recent notifications of any state and then discarded
    // the read ones client-side, so a recipient whose newest 20 were already read saw an empty
    // (or near-empty) panel while hundreds of older unread ones existed — the panel is a
    // "needs my attention" queue, so it should draw from unread, not from recency.
    const [{ items }, unreadCount] = await Promise.all([
      fetchNotifications({ limit: PANEL_PAGE_SIZE, isRead: false }),
      fetchUnreadCount(),
    ]);
    // findForRecipient() now returns dismissed items too (the full notifications page needs
    // them for its history view) — the panel keeps its original "dismissed = gone" behavior by
    // filtering them out here rather than at the API layer.
    dispatch({
      type: 'HYDRATE',
      notifications: items.filter((n: INotification) => !n.dismissedAt).map((n: INotification) => toUiNotification(n, navigate)),
      unreadCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // Backstop against a missed SSE push: the stream's own reconnect-triggered hydrate (below)
  // only fires once the browser notices the connection actually dropped, which can lag behind
  // real life by several seconds — e.g. the API's dev watch-mode process restarting on a file
  // save silently kills the SSE connection without the tab reflecting it immediately. Refetching
  // whenever the tab regains focus (switching back from another app/tab, or waking the machine)
  // catches the panel up on anything that happened while unobserved, independent of whether the
  // stream itself ever noticed the drop.
  React.useEffect(() => {
    const onFocus = () => void hydrate();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [hydrate]);

  useNotificationStream(
    (notification) => {
      dispatch({ type: 'ADD', notification: toUiNotification(notification, navigate) });
    },
    () => void hydrate(),
  );

  // Optimistic: the panel updates immediately, the API call happens in the background. Worst
  // case on failure is a stale read/unread flag until the next hydrate — acceptable for a
  // lightweight global widget, not worth a blocking spinner or error snackbar per click.
  // Both of these re-hydrate once the server confirms: acting on an item only ever *removes* it
  // from the panel's window, so without a backfill the panel drains toward empty and stays there
  // until the next reconnect/focus hydrate — even with hundreds of unread left. The optimistic
  // dispatch still lands first, so the UI updates instantly; the refetch just tops the window up.
  const markRead = React.useCallback(
    (id: string) => {
      dispatch({ type: 'MARK_READ', id });
      markNotificationRead(id)
        .then(hydrate)
        .then(invalidatePage)
        .catch((err: unknown) => console.error('Failed to mark notification read', err));
    },
    [hydrate, invalidatePage],
  );

  const markAllRead = React.useCallback(() => {
    dispatch({ type: 'MARK_ALL_READ' });
    markAllNotificationsRead()
      .then(hydrate)
      .then(invalidatePage)
      .catch((err: unknown) => console.error('Failed to mark all notifications read', err));
  }, [hydrate, invalidatePage]);

  const dismiss = React.useCallback(
    (id: string) => {
      dispatch({ type: 'DISMISS', id });
      dismissNotification(id)
        .then(hydrate)
        .then(invalidatePage)
        .catch((err: unknown) => console.error('Failed to dismiss notification', err));
    },
    [hydrate, invalidatePage],
  );

  const syncRead = React.useCallback((id: string) => dispatch({ type: 'SYNC_READ', id }), []);
  const syncUnread = React.useCallback((id: string) => dispatch({ type: 'SYNC_UNREAD', id }), []);
  const syncAllRead = React.useCallback(() => dispatch({ type: 'MARK_ALL_READ' }), []);
  const syncDismiss = React.useCallback(
    (id: string, wasUnread: boolean) => dispatch({ type: 'SYNC_DISMISS', id, wasUnread }),
    [],
  );

  const value: NotificationContextValue = {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    markRead,
    markAllRead,
    dismiss,
    refresh: hydrate,
    syncRead,
    syncUnread,
    syncAllRead,
    syncDismiss,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
  const ctx = React.useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}
